import { Pressable, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  AnchoredMenu,
  Avatar,
  resolveTheme,
  SettingsMenuView,
  useDisclosure,
  type AnchoredMenuAlign,
  type AnchoredMenuPlacement,
  type SettingsMenuSection,
  type UiTheme,
} from "@heraion/ui";

import { useThemePalette } from "./theme";
import type { Experience } from "./experience";
import type { MeKey } from "./shell/meNav";
import { stateAria } from "@heraion/ui";

// The web account menu (shell chrome) — the identity slot, top-right in the
// TopBar. It gathers everything "about me and my account", the web convention
// that holds for any user (a coach whose primary role is coach, an athlete-only
// user, someone with both): the active profile (role switch), the Me sections,
// Settings, and sign-out. The role switch used to be a row of bordered buttons
// in the rail; it lives here now, and single-role users never see it.
//
// Presentation is the shared SettingsMenuView in `dense` mode (a tight menu
// list, not description cards), driven by useExperience's state (switch) and the
// meSub (open Me section). Two SettingsMenuView instances because each has its
// own single-selection: the active profile vs the open Me section. Selecting a
// row dispatches to one of the three typed callbacks the shell owns.

export function AccountMenu({
  email,
  activeKey,
  activeSettings = false,
  experiences,
  activeExperience,
  onSelectMe,
  onSelectExperience,
  onOpenSettings,
  onSignOut,
  placement,
  align,
  compact = false,
}: {
  email: string;
  /** The open Me section (highlighted), or null. */
  activeKey: MeKey | null;
  /** The Settings surface is the active one — highlights its row. */
  activeSettings?: boolean;
  experiences: Experience[];
  activeExperience: Experience;
  onSelectMe: (key: MeKey) => void;
  onSelectExperience: (e: Experience) => void;
  onOpenSettings: () => void;
  onSignOut: () => void;
  placement: AnchoredMenuPlacement;
  align: AnchoredMenuAlign;
  /** Narrow header trigger: avatar only, no email label. */
  compact?: boolean;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t } = useTranslation();
  const disc = useDisclosure();
  const styles = makeStyles(theme);

  const close = () => disc.setExpanded(false);

  const roleSection: SettingsMenuSection = {
    label: t("experience.menuLabel", { defaultValue: "Active profile" }),
    rows: experiences.map((e) => ({
      key: e,
      title: t(`experience.${e}`),
      sub: "",
    })),
  };

  const meSection: SettingsMenuSection = {
    label: t("mobile.tabs.me", { defaultValue: "Me" }),
    rows: [
      // Mobile Me's order (#315): Goals, MAXes, then Body, then Coach.
      { key: "targets", title: t("mobile.me.navTargets", { defaultValue: "Goals" }), sub: "" },
      { key: "maxes", title: t("mobile.profile.navMaxes", { defaultValue: "MAXes" }), sub: "" },
      {
        key: "corpo",
        title: t("mobile.body.measurementsTitle", { defaultValue: "Measurements" }),
        sub: "",
      },
      { key: "painMap", title: t("bodymap.painTitle"), sub: "" },
      { key: "coach", title: t("coach.title", { defaultValue: "Coach & athletes" }), sub: "" },
    ],
  };

  // Settings + sign-out ride the SAME dense SettingsMenuView (no hand-rolled
  // rows). They're actions, not a selectable section, so no selectedKey matches
  // them — the accent bar just stays idle. onSelect dispatches by key.
  const accountSection: SettingsMenuSection = {
    label: t("mobile.settings.sectionAccount", { defaultValue: "Account" }),
    rows: [
      { key: "settings", title: t("mobile.tabs.settings", { defaultValue: "Settings" }), sub: "" },
      { key: "signout", title: t("app.signOut"), sub: "" },
    ],
  };

  return (
    <AnchoredMenu
      theme={theme}
      open={disc.expanded}
      onClose={close}
      dismissLabel={t("mobile.common.close", { defaultValue: "Close" })}
      placement={placement}
      align={align}
      minWidth={300}
      trigger={
        <Pressable
          onPress={disc.toggle}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel={email}
          accessibilityState={{
            expanded: disc.expanded,
            selected: activeKey !== null || activeSettings,
          }}
          {...stateAria(
            { expanded: disc.expanded, selected: activeKey !== null || activeSettings },
            "button",
          )}
          style={({ pressed }) => [
            styles.trigger,
            compact && styles.triggerCompact,
            (pressed || disc.expanded) && styles.triggerOn,
          ]}
        >
          <Avatar theme={theme} name={email} size={32} />
          {compact ? null : (
            <Text style={styles.triggerLabel} numberOfLines={1}>
              {email}
            </Text>
          )}
          {/* "A Me/account sub-surface is open" — a persistent accent dot, a
              shape cue distinct from the transient pressed fill (never colour
              alone). */}
          {activeKey !== null || activeSettings ? (
            <View style={styles.activeDot} />
          ) : null}
        </Pressable>
      }
    >
      <View style={styles.menuBody}>
        {experiences.length > 1 ? (
          <>
            <SettingsMenuView
              theme={theme}
              dense
              sections={[roleSection]}
              selectedKey={activeExperience}
              onSelect={(key) => {
                close();
                onSelectExperience(key as Experience);
              }}
            />
            <View style={styles.sep} />
          </>
        ) : null}

        <SettingsMenuView
          theme={theme}
          dense
          sections={[meSection, accountSection]}
          // Highlights the open Me section, or the Settings row when the
          // Settings surface is the active one (so re-opening the menu says
          // "you are here" — never state-by-absence). Sign-out never matches.
          selectedKey={activeKey ?? (activeSettings ? "settings" : null)}
          onSelect={(key) => {
            close();
            if (key === "settings") onOpenSettings();
            else if (key === "signout") onSignOut();
            else onSelectMe(key as MeKey);
          }}
        />
      </View>
    </AnchoredMenu>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    trigger: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s3,
      minHeight: 44,
      paddingHorizontal: s.s2,
      borderRadius: r.r4,
      borderWidth: 1,
      borderColor: "transparent",
    },
    triggerCompact: { paddingHorizontal: s.s1 },
    triggerOn: { backgroundColor: p.surface2, borderColor: p.border },
    triggerLabel: { flex: 1, minWidth: 0, color: p.ink2, fontSize: ty.sizes.sm },
    activeDot: {
      width: 10,
      height: 10,
      borderRadius: 5,
      backgroundColor: p.brand500,
    },
    menuBody: { paddingHorizontal: s.s2 },
    sep: { height: 1, backgroundColor: p.border, marginVertical: s.s1 },
  });
}
